import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AuthContext } from "../../components/AuthWrapper";
import toast from "react-hot-toast";
import { AdvisorCheckInInfo, DataToCheckIn } from "../../api/client";
import ConfirmationModal from "../../components/ConfirmModal";
import { Container } from "../../components/Container";
import PageTitle from "../../components/PageTitle";
import { Heading } from "../../components/UiComponents/heading";
import { Button } from "../../components/UiComponents/button";

export default function CheckInComponent(): JSX.Element {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [fullName, setFullName] = useState<string>('');
  const [universityId, setUniversityId] = useState<string>('');
  const [userId, setUserId] = useState<string>(''); // State for userId
  const [advisors, setAdvisors] = useState<AdvisorCheckInInfo[]>([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState<string>('');

  const inputField = useRef<HTMLInputElement>(null);

  const { client }: AuthContext = useOutletContext();

  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        const resp = await client?.student_GetAdvisors();
        setAdvisors(resp ?? []);
      } catch (error) {
        toast.error("Failed to get advisor list");
      }
    }

    fetchAdvisors();
  },[])


  const onCheckInClick = async ()  => {
    if (selectedAdvisor == null || selectedAdvisor == '') {
      return toast.error('Please select an advisor');
    }
    getStudentNameByUID()
  }

  // Fetch student name by UID
  const getStudentNameByUID = async () => {
    if (universityId.length < 8) {
      toast.error('University ID must be at least 8 characters long.');
      return;
    } else if (universityId.length > 8) {
      toast.error('University ID must not exceed 8 characters.');
      return;
    }

    if (!/^\d+$/.test(universityId)) {
      toast.error('University ID must contain only numeric characters.');
      return;
    }

    try {
      const response = await client?.student_GetStudentNameByUId("U" + universityId);
      setFullName(response!.fullName!);
      setUserId(response!.id!); // Set the userId in state
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching student name:', error);
      toast.error('An error occurred while fetching the student name.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    if (inputField.current) {
      inputField.current.value = "";  // Clear the input field
    }
    setUniversityId("");
    setUserId("");
    setFullName("");
    setSelectedAdvisor("");
  };

  // Check in student
  const checkInStudent = async () => {
    const studentToCheckIn = new DataToCheckIn({
      userId: userId,
      uid: "U" + universityId,
      advisorId: selectedAdvisor,
    });
    await client?.student_CheckInStudent(studentToCheckIn)
      .then(() => {
        toast.success(`${fullName} checked in successfully!`);
        setUniversityId("");
        setUserId("");
        setFullName("");
        setSelectedAdvisor("");
        setShowModal(false); // Close the modal after successful check-in
        if (inputField.current) {
          inputField.current.value = "";  // Clear the input field
        }
      })
      .catch((error) => {
        console.error('Error during student check-in:', error);
        toast.error('An error occurred during the check-in process.');
      });
  };

  return (
    <Container>
      <PageTitle title="Student Check-In" />
      <Heading text="Check In for Appointment with ONS"/>
      <div className="flex flex-col justify-content-center">
        <label
          htmlFor="UidEnter"
          className="block text-2xl font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          Please Enter Your UID (without U)
        </label>

        <input
          ref={inputField}
          id={"UidEnter"}
          type="text"
          placeholder={"123456789"}
          className={"w-full h-16 text-2xl text-black rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-500 focus:border-green-600 mb-3 p-3"}
          onChange={event => setUniversityId(event.target.value)}
        />

        <label
          htmlFor="advisorEnter"
          className="block text-2xl font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          Select the advisor you are here to see:
        </label>

        <select
          id="AdvisorSelect"
          className="w-full h-16 text-2xl text-black rounded-2xl focus:outline-none focus:ring-4 focus:ring-green-500 focus:border-green-600 mb-3 p-3"
          value={selectedAdvisor}
          onChange={(e) => setSelectedAdvisor(e.target.value)}
        >
          <option value=''>---Select advisor---</option>
          {advisors.map((advisor) => (
            <option key={advisor.userId} value={advisor.userId}>{advisor.fullName}</option>
          ))}

        </select>
        {/* The className of the Button below should NOT be deleted because it's styled specially for a special appearance */}
        <Button
          className={"w-full h-12 text-2xl font-bold"}
          onClick={onCheckInClick}
          abbr="Click to check in. Your name will show up in the queue and will be processed by admins."
        >
          Check In
        </Button>
      </div>
      <ConfirmationModal
        Id=""
        isOpen={showModal}
        onClose={closeModal}
        onConfirm={checkInStudent}
        TextMessage={`Is ${fullName} your name?`}
        isGreen
        abbrCancel="No, it is not"
        abbrConfirm="Yes, it is"
      >
      </ConfirmationModal>
    </Container>
  );
}


